
import { useCookie } from '@core/composable/useCookie'

export function getDefaultDashboard(userRole) {
  // Money / accounting clerk lands on invoices — the only screen they
  // need. Anyone else with a recognized role (or any logged-in user)
  // lands on the tracking dashboard. Falling through to /login was
  // a real bug 2026-05-10: superadmin wasn't in the explicit list and
  // got bounced back to login on every redirect.
  if (userRole === 'money') return { name: 'shipment-warehouse-invoices' }

  // Catch-all for authenticated users — tracking dashboard is the
  // global entrypoint. Includes admin, superadmin, manager, user,
  // resident, mobile-admin, team-manager, etc.
  if (userRole) return { name: 'shipment-tracking' }

  // No role / not logged in
  return { name: 'login' }
}

// 👉 Redirects - Simplified to only essential routes
export const redirects = [
  // NOTE: Always redirect to the tracking dashboard for authenticated users
  {
    path: '/',
    name: 'index',
    redirect: to => {
      const userData = useCookie('userData')
      const userRole = userData.value?.role

      // If user is logged in, redirect to their default dashboard.
      // Money clerk lands on /invoices; everyone else on /tracking.
      if (userRole) {
        return getDefaultDashboard(userRole)
      }

      // For guests, go to login
      return { name: 'login' }
    },
  },

  // Profile page hasn't been implemented yet
  // {
  //   path: '/profile',
  //   name: 'profile',
  //   component: () => import('@/pages/pages/account/profile.vue'),
  //   meta: {
  //     layout: 'default',
  //     action: 'read',
  //     subject: 'profile',
  //     pageTitle: 'Profile',
  //     breadcrumb: [
  //       {
  //         title: 'Home',
  //         to: '/',
  //       },
  //       {
  //         title: 'Profile',
  //         active: true,
  //       },
  //     ],
  //   },
  // },
  {
    name: 'default-authorized-page',
    path: '/dashboard',
    redirect: { name: 'shipment-tracking' },
  },
]
export const routes = [
  // User Profile Routes
  {
    path: '/account-settings/:tab',
    name: 'account-settings-tab',
    component: () => import('@/views/account-settings/AccountSettings.vue'),
    meta: {
      layout: 'default',

      // Remove the permission check for settings
      // action: 'read',
      // subject: 'Auth',
      pageTitle: 'Account Settings',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'Account Settings',
          active: true,
        },
      ],
    },
  },
  {
    path: '/user-profile/:tab',
    name: 'user-profile-tab',
    component: () => import('@/views/user-profile/UserProfile.vue'),
    meta: {
      layout: 'default',

      // Remove the permission check for user profile
      // action: 'read',
      // subject: 'Auth',
      pageTitle: 'User Profile',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'Profile',
          active: true,
        },
      ],
    },
  },
  {
    path: '/track',
    name: 'track',
    component: () => import('@/views/shipment/tracking/CustomerTrack.vue'),
    meta: {
      layout: 'blank',
      public: true,
    },
  },
  // Remove the reset-password route from Vue Router
  // Let Laravel handle /reset-password directly
  // {
  //   path: '/reset-password',
  //   name: 'reset-password',
  //   beforeEnter() {
  //     // Redirect to the Laravel Blade template for password reset
  //     window.location.href = '/reset-password'
  //     return false
  //   },
  // },

  // User Management Routes
  {
    path: '/user-management/users',
    name: 'user-management-users',
    component: () => import('@/views/user-management/Users.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Users',
      pageTitle: 'Users',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'User Management',
          to: '/user-management',
        },
        {
          title: 'Users',
          active: true,
        },
      ],
    },
  },
  {
    path: '/user-management/roles',
    name: 'user-management-roles',
    component: () => import('@/views/user-management/Roles.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Roles',
      pageTitle: 'Roles',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'User Management',
          to: '/user-management',
        },
        {
          title: 'Roles',
          active: true,
        },
      ],
    },
  },
  {
    path: '/user-management/teams',
    name: 'user-management-teams',
    component: () => import('@/views/user-management/Teams.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Teams',
      pageTitle: 'Teams',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'User Management',
          to: '/user-management',
        },
        {
          title: 'Teams',
          active: true,
        },
      ],
    },
  },
  {
    path: '/user-management/permissions',
    name: 'user-management-permissions',
    component: () => import('@/views/user-management/Permissions.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Permissions',
      pageTitle: 'Permissions',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'User Management',
          to: '/user-management',
        },
        {
          title: 'Permissions',
          active: true,
        },
      ],
    },
  },

  // Shipment Routes
  {
    path: '/shipment/incoming-parcels/create',
    name: 'shipment-incoming-parcels-create',
    component: () => import('@/views/shipment/incoming-parcels/Create.vue'),
    meta: {
      requiresAuth: true,
      action: 'manage',
      subject: 'All',
      // navActiveLink: 'shipment-incoming-parcels',
    },
  },
  {
    path: '/shipment/incoming-parcels/create-group',
    name: 'shipment-incoming-parcels-create-group',
    component: () => import('@/views/shipment/incoming-parcels/CreateGroup.vue'),
    meta: {
      layout: 'default',
      action: 'create',
      subject: 'Parcel',
      pageTitle: 'Create Group Parcels',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'Shipment',
          to: '/shipment',
        },
        {
          title: 'Incoming Parcels',
          to: '/shipment/incoming-parcels',
        },
        {
          title: 'Create Group',
          active: true,
        },
      ],
    },
  },
  {
    path: '/shipment/incoming-parcels/:id',
    name: 'shipment-incoming-parcels-view',
    component: () => import('@/pages/shipment/incoming-parcels/[id].vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissions: ['view-GB-parcels', 'view-GD-parcels', 'view-JP-parcels'],
      navActiveLink: 'shipment-incoming-parcels',
      pageTitle: 'Parcel Details',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'Shipment',
          to: '/shipment',
        },
        {
          title: 'Incoming Parcels',
          to: '/shipment/incoming-parcels',
        },
        {
          title: 'Details',
          active: true,
        },
      ],
    },
  },
  {
    path: '/shipment/incoming-parcels/:id/edit',
    name: 'shipment-incoming-parcels-edit',
    component: () => import('@/views/shipment/incoming-parcels/Edit.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      action: 'edit',
      subject: 'IncomingParcels',
      navActiveLink: 'shipment-incoming-parcels-edit',
      pageTitle: 'Edit Parcel',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'Shipment',
          to: '/shipment',
        },
        {
          title: 'Incoming Parcels',
          to: '/shipment/incoming-parcels',
        },
        {
          title: 'Edit',
          active: true,
        },
      ],
    },
  },
  {
    path: '/shipment/outgoing-packages/create',
    name: 'shipment-outgoing-packages-create',
    component: () => import('@/views/shipment/outgoing-packages/Create.vue'),
    meta: {
      requiresAuth: true,
      action: 'manage',
      subject: 'All',
      // navActiveLink: 'shipment-outgoing-packages',
    },
  },
  {
    path: '/shipment/outgoing-packages/:id/edit',
    name: 'shipment-outgoing-packages-edit',
    component: () => import('@/views/shipment/outgoing-packages/Edit.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      action: 'manage',
      subject: 'All',
      navActiveLink: 'shipment-outgoing-packages',
    },
  },
  {
    path: '/shipment/packing-lists/create',
    name: 'shipment-packing-lists-create',
    component: () => import('@/views/shipment/packing-lists/Create.vue'),
    meta: {
      // navActiveLink: 'shipment-packing-lists',
    },
  },
  {
    path: '/shipment/packing-lists/:id/edit',
    name: 'shipment-packing-lists-edit',
    component: () => import('@/views/shipment/packing-lists/Edit.vue'),
    props: true,
    meta: {
      navActiveLink: 'shipment-packing-lists',
    },
  },
  {
    path: '/shipment/packing-lists/:id/view',
    name: 'shipment-packing-lists-view',
    component: () => import('@/pages/shipment/packing-lists/[id]/view.vue'),
    props: true,
    meta: {
      navActiveLink: 'shipment-packing-lists',
    },
  },
  {
    path: '/shipment/container-arrivals/create',
    name: 'shipment-container-arrivals-create',
    component: () => import('@/views/shipment/container-arrivals/Create.vue'),
    meta: {
      // navActiveLink: 'shipment-container-arrivals',
    },
  },
  {
    path: '/shipment/container-arrivals/:id/edit',
    name: 'shipment-container-arrivals-edit',
    component: () => import('@/views/shipment/container-arrivals/Edit.vue'),
    props: true,
    meta: {
      navActiveLink: 'shipment-container-arrivals',
    },
  },
  {
    path: '/shipment/products/:id/edit',
    name: 'shipment-products-edit',
    component: () => import('@/views/shipment/products/Edit.vue'),
    props: true,
    meta: {
      navActiveLink: 'shipment-products',
    },
  },
  {
    path: '/shipment/customers/create',
    name: 'shipment-customers-create',
    component: () => import('@/views/shipment/customers/Create.vue'),
    meta: {
      // navActiveLink: 'shipment-customers',
    },
  },
  {
    path: '/shipment/customers/:id/edit',
    name: 'shipment-customers-edit',
    component: () => import('@/views/shipment/customers/Edit.vue'),
    props: true,
    meta: {
      navActiveLink: 'shipment-customers',
    },
  },
  {
    path: '/shipment/tracking',
    name: 'shipment-tracking',
    component: () => import('@/views/shipment/tracking/Dashboard.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Dashboard',
      pageTitle: 'Tracking Dashboard',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'Shipment',
          to: '/shipment',
        },
        {
          title: 'Tracking',
          active: true,
        },
      ],
    },
  },
  {
    path: '/shipment/warehouse',
    name: 'shipment-warehouse',
    component: () => import('@/views/shipment/WarehouseOperations.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Parcels',
      pageTitle: 'Warehouse Operations',
      breadcrumb: [
        { title: 'Home', to: '/' },
        { title: 'Shipment', to: '/shipment' },
        { title: 'Warehouse Operations', active: true },
      ],
    },
  },
  {
    path: '/history/customers/:customerId',
    name: 'history-customers-detail',
    component: () => import('@/pages/history/customers/[customerId].vue'),
    props: true,
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Parcel',
      pageTitle: 'Customer History Details',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'History',
          to: '/history',
        },
        {
          title: 'Customer History',
          to: '/history/customers',
        },
        {
          title: 'Details',
          active: true,
        },
      ],
    },
  },
  {
    path: '/history/containers',
    name: 'history-containers',
    component: () => import('@/pages/history/containers/index.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Parcel',
      pageTitle: 'Container History',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'History',
          to: '/history',
        },
        {
          title: 'Container History',
          active: true,
        },
      ],
    },
  },
  {
    path: '/history/containers/:containerId',
    name: 'history-containers-detail',
    component: () => import('@/pages/history/containers/[containerId].vue'),
    props: true,
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Parcel',
      pageTitle: 'Container History Details',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'History',
          to: '/history',
        },
        {
          title: 'Container History',
          to: '/history/containers',
        },
        {
          title: 'Details',
          active: true,
        },
      ],
    },
  },
  {
    path: '/history/reports',
    name: 'history-reports',
    component: () => import('@/pages/history/reports/index.vue'),
    meta: {
      layout: 'default',
      action: 'read',
      subject: 'Parcel',
      pageTitle: 'Reports & Analytics',
      breadcrumb: [
        {
          title: 'Home',
          to: '/',
        },
        {
          title: 'History',
          to: '/history',
        },
        {
          title: 'Reports & Analytics',
          active: true,
        },
      ],
    },
  },
]
